import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AdminTable } from "./AdminTable";
import { Plus, Star } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", role: "", location: "", rating: 5, quote: "", avatar: "", featured: false };

export default function AdminTestimonials() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/testimonials").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm(r); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, rating: Number(form.rating) };
    try {
      if (editing) await api.put(`/admin/testimonials/${editing.id}`, payload);
      else await api.post("/admin/testimonials", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (r) => {
    if (!window.confirm("Delete this testimonial?")) return;
    await api.delete(`/admin/testimonials/${r.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-serif text-4xl">Testimonials</h1><p className="text-foreground/60 mt-1">Upload client & student reviews.</p></div>
        <Button data-testid="new-testimonial-btn" onClick={openNew} className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold"><Plus className="w-4 h-4 mr-1" />New</Button>
      </div>

      <AdminTable
        testIdPrefix="t-row"
        columns={[
          { key: "name", label: "Name" },
          { key: "role", label: "Role" },
          { key: "rating", label: "Rating", render: (r) => <span className="text-[#D4AF37] inline-flex">{[...Array(r.rating)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-current" />)}</span> },
          { key: "featured", label: "Featured", render: (r) => r.featured ? "★" : "" },
        ]}
        rows={rows}
        onEdit={openEdit}
        onDelete={del}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Testimonial</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Role</Label><Input value={form.role || ""} onChange={(e) => setForm({ ...form, role: e.target.value })} /></div>
              <div><Label>Location</Label><Input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
            </div>
            <div><Label>Rating (1-5)</Label><Input type="number" min={1} max={5} value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} /></div>
            <div><Label>Quote</Label><Textarea rows={4} required value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} /></div>
            <div className="flex items-center gap-3"><Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} /><Label>Featured on home page</Label></div>
            <Button data-testid="save-testimonial-btn" type="submit" className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold">Save</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
